import type {
  EquipmentInstance,
  EquipmentStats,
  ItemDef,
  Player,
  ResourceCost,
  ResourceId,
} from './types';
import { getItem } from './items';

export const MAX_UPGRADE_LEVEL = 10;

/**
 * Additive upgrade scheme: +1 ATK/DEF, +2 HP per level, only on stats the
 * item *design* has > 0 (checked via `originalBase` = ItemDef.stats).
 * This way a low-rolled stat still gains upgrade bonuses.
 */
export function upgradedStats(
  base: EquipmentStats,
  level: number,
  originalBase?: EquipmentStats,
): EquipmentStats {
  const ref = originalBase ?? base;
  return {
    attack:  ref.attack  > 0 ? base.attack  + level     : base.attack,
    defense: ref.defense > 0 ? base.defense + level     : base.defense,
    health:  ref.health  > 0 ? base.health  + level * 2 : base.health,
  };
}

export function instanceStats(instance: EquipmentInstance): EquipmentStats {
  const def = getItem(instance.itemId);
  if (!def?.stats) return { attack: 0, defense: 0, health: 0 };
  const base = instance.rolledStats ?? def.stats;
  return upgradedStats(base, instance.upgradeLevel, def.stats);
}

export function maxLevel(def: ItemDef): number {
  return def.maxUpgradeLevel ?? MAX_UPGRADE_LEVEL;
}

/** Cost to go from currentLevel to currentLevel + 1. */
export function upgradeCost(def: ItemDef, currentLevel: number): ResourceCost {
  const basePrice = Math.max(10, def.baseValue * 0.25);
  const mult = Math.pow(1.2, currentLevel);
  const credits = Math.floor(basePrice * mult);
  const cost: ResourceCost = { credits };
  cost.scrap = (currentLevel + 1) * 3;
  if (currentLevel >= 3) cost.nano_chip = (currentLevel - 2) * 2;
  if (currentLevel >= 6) cost.data_track = currentLevel - 5;
  if (currentLevel >= 9) cost.hyper_processor_5 = 1;
  return cost;
}

/** How many units of a resource does the player own? */
export function playerHas(player: Player, resource: 'credits' | 'premium' | ResourceId): number {
  if (resource === 'credits') return player.credits;
  if (resource === 'premium') return player.premium;
  const stack = player.inventory.slots.find((s) => s.itemId === resource);
  return stack?.quantity ?? 0;
}

export function canAfford(player: Player, cost: ResourceCost): boolean {
  for (const [key, needed] of Object.entries(cost) as Array<[keyof ResourceCost, number]>) {
    if (!needed) continue;
    if (playerHas(player, key as 'credits' | 'premium' | ResourceId) < needed) return false;
  }
  return true;
}

/** Spend a cost immutably. Caller must check canAfford first. */
export function spendCost(player: Player, cost: ResourceCost): Player {
  let next: Player = { ...player };
  let credits = next.credits;
  let premium = next.premium;
  let slots = next.inventory.slots;

  for (const [key, amount] of Object.entries(cost) as Array<[keyof ResourceCost, number]>) {
    if (!amount) continue;
    if (key === 'credits') {
      credits -= amount;
    } else if (key === 'premium') {
      premium -= amount;
    } else {
      slots = slots
        .map((s) => (s.itemId === key ? { ...s, quantity: s.quantity - amount } : s))
        .filter((s) => s.quantity > 0);
    }
  }

  next = {
    ...next,
    credits,
    premium,
    inventory: { ...next.inventory, slots },
    updatedAt: Date.now(),
  };
  return next;
}

/** Returns cost lines for UI display. */
export function formatCost(cost: ResourceCost): Array<{ key: string; amount: number }> {
  const out: Array<{ key: string; amount: number }> = [];
  for (const [k, v] of Object.entries(cost)) {
    if (v && v > 0) out.push({ key: k, amount: v });
  }
  return out;
}
